import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, FileText, CheckCircle, Clock, Mail } from "lucide-react";
import QuoteComparisonReport from "./QuoteComparisonReport";
import SpeakToProfessionalModal from "./SpeakToProfessionalModal";
import StartNewRequestModal from "./StartNewRequestModal";
import { QuoteRequest, RateAnalysis } from "../types/rate-analysis";
import { useAuth } from "../contexts/AuthContext";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface QuoteAnalysisPageProps {
  quoteRequest: QuoteRequest | undefined;
  analysis: RateAnalysis | null;
  disableTracking?: boolean;
}

const QuoteAnalysisPage: React.FC<QuoteAnalysisPageProps> = ({
  quoteRequest,
  analysis,
  disableTracking = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewRequestModalOpen, setIsNewRequestModalOpen] = useState(false);
  const [isUpdateRequestModalOpen, setIsUpdateRequestModalOpen] =
    useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState<
    "text" | "phone" | "email" | ""
  >("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isEmailSubmitted, setIsEmailSubmitted] = useState(false);

  const hasLoggedView = useRef(false);
  const { user } = useAuth();

  // Set initial contact info state based on quote request
  useEffect(() => {
    if (quoteRequest?.email || quoteRequest?.phone) {
      if (quoteRequest.email) {
        setEmail(quoteRequest.email);
      }
      if (quoteRequest.phone) {
        setPhone(quoteRequest.phone);
      }
      setIsEmailSubmitted(true);
    }
  }, [quoteRequest?.email, quoteRequest?.phone]);

  useEffect(() => {
    if (
      analysis &&
      quoteRequest?.id &&
      !hasLoggedView.current &&
      !disableTracking
    ) {
      const body: Record<string, string> = {};
      if (user && user.email) {
        body.viewed_by = user.email;
      }
      fetch(`${API_BASE_URL}/analysis/${quoteRequest.id}/viewed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
      });
      hasLoggedView.current = true;
    }
  }, [analysis, quoteRequest?.id, user, disableTracking]);

  const handleContactInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError("");
    setPhoneError("");

    // Validate email if email is selected
    if (preferredContactMethod === "email" && !email) {
      setEmailError("Email is required");
      return;
    }
    if (
      preferredContactMethod === "email" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Validate phone if phone is selected
    if (preferredContactMethod === "phone" && !phone) {
      setPhoneError("Phone number is required");
      return;
    }
    if (
      preferredContactMethod === "phone" &&
      !/^\+?[1-9]\d{1,14}$/.test(phone.replace(/\D/g, ""))
    ) {
      setPhoneError("Please enter a valid phone number");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/quote-requests/${quoteRequest?.id}/contact-info`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            phone,
            preferredContactMethod,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save contact information");
      }

      setIsEmailSubmitted(true);
    } catch (error) {
      console.error("Error saving contact information:", error);
    }
  };

  const handleStartNewRequest = () => {
    localStorage.removeItem("quote_request_id");
    window.location.href = "/check-my-rate";
  };

  // If we have a request ID but no analysis yet, show the analyzing state
  if (!analysis) {
    return (
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
          Your Quote Analysis is in Progress
        </h1>
        <p className="text-xl text-gray-600 pb-2">
          We're reviewing your mortgage quote to provide you with the best
          possible comparison
        </p>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-8 px-2">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mb-2">
                  <CheckCircle size={20} />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Information Submitted
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-primary mx-6"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mb-2">
                  <Clock size={20} />
                </div>
                <span className="text-sm font-medium text-gray-900">
                  Quote Review
                </span>
              </div>
              <div className="flex-1 h-0.5 bg-gray-200 mx-6"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-400 flex items-center justify-center mb-2">
                  <FileText size={20} />
                </div>
                <span className="text-sm font-medium text-gray-500">
                  Results Ready
                </span>
              </div>
            </div>

            <div className="bg-primary-light/10 p-6 rounded-md mb-8">
              <h4 className="text-lg font-medium text-primary mb-4">
                What to Expect
              </h4>
              {quoteRequest?.broker_rate ? (
                <ul className="space-y-3 text-primary">
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>
                      Your results will be ready soon (typically between 1 and
                      24 hours)
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>
                      We'll compare your rate quote against local alternatives
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>
                      You'll receive a detailed analysis of your current quote
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>All information is kept anonymous and secure</span>
                  </li>
                </ul>
              ) : (
                <ul className="space-y-3 text-primary">
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>
                      Your quotes will be ready soon (typically between 1 and 24
                      hours)
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>
                      Your quotes will be more competitive than from larger
                      mortgage companies
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle
                      size={18}
                      className="mr-2 mt-1 flex-shrink-0"
                    />
                    <span>All information is kept anonymous and secure</span>
                  </li>
                </ul>
              )}
            </div>

            {/* Email Notification Section */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <Mail className="h-5 w-5 text-primary hidden md:block" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Optional - Get Notified When Your Analysis is Ready
                </h3>
              </div>
              <p className="text-gray-600 mb-6">
                Check back later or give us your info so we can notify you when
                your quote analysis is complete. This is a no-spam zone, we
                promise.
              </p>

              {!isEmailSubmitted ? (
                <form onSubmit={handleContactInfoSubmit} className="space-y-4">
                  <div className="text-left">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Contact Method
                    </label>
                    <div className="flex gap-4 justify-start">
                      <label className="flex items-center cursor-pointer gap-2">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="text"
                          checked={preferredContactMethod === "text"}
                          onChange={() => setPreferredContactMethod("text")}
                          className="h-4 w-4 accent-primary focus:ring-primary focus:ring-offset-0"
                        />
                        <span className="text-sm text-gray-700">Text</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="phone"
                          checked={preferredContactMethod === "phone"}
                          onChange={() => setPreferredContactMethod("phone")}
                          className="h-4 w-4 accent-primary focus:ring-primary focus:ring-offset-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Phone
                        </span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="contactMethod"
                          value="email"
                          checked={preferredContactMethod === "email"}
                          onChange={() => setPreferredContactMethod("email")}
                          className="h-4 w-4 accent-primary focus:ring-primary focus:ring-offset-0"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Email
                        </span>
                      </label>
                    </div>
                  </div>

                  {preferredContactMethod === "email" && (
                    <div className="text-left">
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                          emailError ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="Enter your email"
                      />
                      {emailError && (
                        <p className="mt-1 text-sm text-red-600">
                          {emailError}
                        </p>
                      )}
                    </div>
                  )}

                  {(preferredContactMethod === "phone" ||
                    preferredContactMethod === "text") && (
                    <div className="text-left">
                      <label
                        htmlFor="phone"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                          phoneError ? "border-red-500" : "border-gray-300"
                        }`}
                        placeholder="Enter your phone number"
                      />
                      {phoneError && (
                        <p className="mt-1 text-sm text-red-600">
                          {phoneError}
                        </p>
                      )}
                    </div>
                  )}

                  <button type="submit" className="w-full btn-primary">
                    Notify Me
                  </button>
                </form>
              ) : (
                <div className="text-center">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-gray-900 font-medium">
                    {preferredContactMethod === "email" ||
                    preferredContactMethod === "" ? (
                      <>
                        We'll email you as soon as your analysis is ready.
                        <br />
                        <span className="text-sm text-gray-600">
                          If you don't see our email soon, please check your
                          spam or junk folder
                        </span>
                      </>
                    ) : preferredContactMethod === "text" ? (
                      <>We'll text you as soon as your analysis is ready.</>
                    ) : (
                      <>We'll call you as soon as your analysis is ready.</>
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="pt-4 flex flex-col sm:flex-row justify-center gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Or, talk to us now</p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="btn-secondary inline-flex items-center justify-center space-x-2"
                >
                  <span>Speak With a Mortgage Expert</span>
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="space-y-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <details className="group">
                <summary className="flex justify-between items-center p-4 cursor-pointer bg-white hover:bg-gray-50">
                  <h3 className="text-lg font-medium text-gray-800">
                    Why is this taking time?
                  </h3>
                  <span className="transition group-open:rotate-180">
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M6 9l6 6 6-6"
                      ></path>
                    </svg>
                  </span>
                </summary>
                <div className="p-4 pt-0 text-gray-700">
                  <p>
                    We review each quote request as they come in. We want to
                    give you actionable feedback and advice that will hopefully
                    help you save money and get the best rate possible. We feel
                    that automated quotes don't help buyers as much as human
                    feedback.
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>

        <SpeakToProfessionalModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    );
  }

  // If we have both request ID and analysis, show the results
  return (
    <>
      <div className="text-center mb-12">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
          Your Rate Quote Analysis
        </h1>
      </div>

      {/* Disclosure and Rate Date Banner */}
      <div className="flex justify-center mb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full max-w-2xl lg:max-w-3xl">
          <div className="flex items-start space-x-3">
            <div className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008H12v-.008z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm text-blue-800">
                <p className="mb-3">
                  Rate Estimates as of{" "}
                  {(() => {
                    // Get the most recent quote date, fallback to request creation date
                    if (
                      quoteRequest &&
                      quoteRequest.quotes &&
                      quoteRequest.quotes.length > 0
                    ) {
                      const mostRecentQuote = quoteRequest.quotes.reduce(
                        (latest, current) =>
                          new Date(current.created_at) >
                          new Date(latest.created_at)
                            ? current
                            : latest
                      );
                      return new Date(
                        mostRecentQuote.created_at
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      });
                    }
                    return new Date(
                      quoteRequest?.created_at || Date.now()
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                  })()}
                  . Rates and pricing will fluctuate daily as the market
                  changes.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <button
                    onClick={() => setIsUpdateRequestModalOpen(true)}
                    className="text-sm text-blue-700 hover:text-blue-800 underline font-medium"
                  >
                    Request updated rates
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <QuoteComparisonReport
        analysis={analysis}
        quoteRequest={quoteRequest}
        gradeTitle="Your Quote Grade"
      />

      <div className="mt-8 flex justify-center space-x-4">
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn-primary inline-flex items-center justify-center space-x-2"
        >
          <span>Speak to a Mortgage Expert</span>
          <ArrowRight size={18} />
        </button>
        <button
          onClick={() => setIsNewRequestModalOpen(true)}
          className="btn-secondary inline-flex items-center space-x-2"
        >
          <FileText size={18} />
          <span>Start New Analysis</span>
        </button>
      </div>

      <SpeakToProfessionalModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        quoteRequestId={quoteRequest?.id}
      />
      <SpeakToProfessionalModal
        isOpen={isUpdateRequestModalOpen}
        onClose={() => setIsUpdateRequestModalOpen(false)}
        isUpdateRequest={true}
        quoteRequestId={quoteRequest?.id}
      />
      <StartNewRequestModal
        isOpen={isNewRequestModalOpen}
        onClose={() => setIsNewRequestModalOpen(false)}
        onConfirm={handleStartNewRequest}
      />
    </>
  );
};

export default QuoteAnalysisPage;
